/**
 * Mac Desktop Redux Slice
 * Manages state for macOS desktop sessions
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  listMacDesktops,
  createMacDesktop,
  getMacDesktop,
  deleteMacDesktop,
  macDesktopAction,
  getMacCredentials
} from "../api/macApi";

// Async thunks
export const fetchMacDesktops = createAsyncThunk(
  "macos/fetchDesktops",
  async (_, { rejectWithValue }) => {
    try {
      const response = await listMacDesktops();
      return response.items || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const launchMacDesktop = createAsyncThunk(
  "macos/launchDesktop",
  async (params, { rejectWithValue }) => {
    try {
      const response = await createMacDesktop(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchMacDesktop = createAsyncThunk(
  "macos/fetchDesktop",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getMacDesktop(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const removeMacDesktop = createAsyncThunk(
  "macos/removeDesktop",
  async (id, { rejectWithValue }) => {
    try {
      await deleteMacDesktop(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const performMacAction = createAsyncThunk(
  "macos/performAction",
  async ({ id, action }, { rejectWithValue }) => {
    try {
      const response = await macDesktopAction(id, action);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchMacCredentials = createAsyncThunk(
  "macos/fetchCredentials",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getMacCredentials(id);
      return { id, credentials: response };
    } catch (error) {
    const status = error.response?.status;
    const message = error.response?.data?.error || error.response?.data?.message || error.message;
    // Mark 409 as a transient, provisioning-in-progress condition so the
    // viewer can keep polling without treating it as a hard error.
    if (status === 409) {
    return rejectWithValue({ transient: true, status, message });
    }
    return rejectWithValue({ transient: false, status, message });
    }
  }
);

const initialState = {
  desktops: [],
  activeDesktop: null,
  credentials: {},
  loading: false,
  error: null,
  actionLoading: {}, // keyed by desktop ID — true while an action is in-flight
  launchStatus: "idle", // idle, loading, succeeded, failed
  connectionStatus: "disconnected" // disconnected, connecting, connected, error
};

const macSlice = createSlice({
  name: "macos",
  initialState,
  reducers: {
    setActiveMacDesktop: (state, action) => {
      state.activeDesktop = action.payload;
    },
    clearActiveMacDesktop: (state) => {
      state.activeDesktop = null;
    },
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateDesktopInList: (state, action) => {
      const index = state.desktops.findIndex((d) => d.id === action.payload.id);
      if (index !== -1) {
        state.desktops[index] = action.payload;
      }
      if (state.activeDesktop?.id === action.payload.id) {
        state.activeDesktop = action.payload;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all desktops
      .addCase(fetchMacDesktops.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMacDesktops.fulfilled, (state, action) => {
        state.loading = false;
        state.desktops = action.payload;
      })
      .addCase(fetchMacDesktops.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Launch desktop
      .addCase(launchMacDesktop.pending, (state) => {
        state.launchStatus = "loading";
        state.error = null;
      })
      .addCase(launchMacDesktop.fulfilled, (state, action) => {
        state.launchStatus = "succeeded";
        state.desktops.push(action.payload);
        state.activeDesktop = action.payload;
      })
      .addCase(launchMacDesktop.rejected, (state, action) => {
        state.launchStatus = "failed";
        state.error = action.payload;
      })

      // Fetch single desktop
      .addCase(fetchMacDesktop.fulfilled, (state, action) => {
        const index = state.desktops.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.desktops[index] = action.payload;
        } else {
          state.desktops.push(action.payload);
        }
        if (state.activeDesktop?.id === action.payload.id) {
          state.activeDesktop = action.payload;
        }
      })

      // Remove desktop
      .addCase(removeMacDesktop.fulfilled, (state, action) => {
        state.desktops = state.desktops.filter((d) => d.id !== action.payload);
        if (state.activeDesktop?.id === action.payload) {
          state.activeDesktop = null;
        }
      })

      // Perform action
      .addCase(performMacAction.pending, (state, action) => {
        const id = action.meta.arg.id;
        state.actionLoading[id] = true;
      })
      .addCase(performMacAction.fulfilled, (state, action) => {
        const id = action.payload.id;
        delete state.actionLoading[id];
        const index = state.desktops.findIndex((d) => d.id === id);
        if (index !== -1) {
          state.desktops[index] = action.payload;
        }
        if (state.activeDesktop?.id === id) {
          state.activeDesktop = action.payload;
        }
      })
      .addCase(performMacAction.rejected, (state, action) => {
        const id = action.meta.arg.id;
        delete state.actionLoading[id];
      })

      // Fetch credentials
      .addCase(fetchMacCredentials.fulfilled, (state, action) => {
        state.credentials[action.payload.id] = action.payload.credentials;
      });
  }
});

export const {
  setActiveMacDesktop,
  clearActiveMacDesktop,
  setConnectionStatus,
  clearError,
  updateDesktopInList
} = macSlice.actions;

export default macSlice.reducer;
